import React from "react";
import { PagingQuery } from "cci-utility";
import InfiniteList from "./InfiniteList";

/**
 * An optimized list that fetches data with a GraphQL Query.
 * 
 * Props:
 * - variables: Key values that will be used in the GraphQL Query to fetch the data from the server.
 * - queryName: Name of the query used to fetch the data. We use this to extract the data from the JSON response.
 * - filterProperty: Name of the variable that will be used to filter (via the filter method) the query response.
 * - fixedHeader: Header that will appear fixed above the rows in the list, regardless of the scrolling done in the list. This is a function called with 3 parameters: 1. Apollo's refetch function that forces data to be re-fetched from the server, 2. The filter function that can be called with the value will be set in the variables so the query can filter the data, 3. Value that the query is currently being filtered by.
 * - onVariablesUpdate: See PagingQuery.
 */
class QueryInfiniteList extends React.Component {
  constructor(props) {
    super(props);

    const { variables, filterProperty } = props;

    this.filter = this.filter.bind(this);
    this.state = { currentFilter: variables[filterProperty] };
  }

  /**
   * Filter the data in the GraphQL Query.
   * 
   * This is done by changing the variables that the query uses to fetch the data from the server.
   */
  filter(value) {
    const { onVariablesUpdate, filterProperty } = this.props;

    this.setState({ currentFilter: value });

    // Update the variables in the context so that an inspect or any other component can refetch the query if needed.
    onVariablesUpdate({[filterProperty]: value});
  }

  render() {
    const { currentFilter } = this.state,
      { queryName, variables, filterProperty, fixedHeader, ...restProps } = this.props;

    // Using this to set new office when switching business or office
    this.props.onVariablesUpdate(variables);

    return (
      <PagingQuery
        queryName={queryName}
        errorPolicy="all"
        // This is done to determine if the list is being refreshed
        notifyOnNetworkStatusChange={true}
        {...restProps}

        variables={{...variables, [filterProperty]: currentFilter}}
      >
        {/* For what this line does, see "Computed object property names and destructuring" in https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment */}
        {({ data: { [queryName]: queryData = {} }, refetch, ...restQueryProps }) => (
          <InfiniteList
            {...restQueryProps}
            {...restProps}
            fixedHeader={fixedHeader && fixedHeader(refetch, this.filter, currentFilter)}
            refetch={refetch}
            rows={queryData && queryData.content}
            totalRows={queryData && queryData.totalElements}
          />
        )}
      </PagingQuery>
    );
  }
}

QueryInfiniteList.defaultProps = {
  variables: {},
  onVariablesUpdate: () => {}
};

export default QueryInfiniteList;
